import type { Catalog } from "../en";

/** Filipino: auth pages (mirrors en/auth.ts). Product terms (2FA, OTP, workspace) kept as used in PH workplaces. */
export const auth: Catalog["auth"] = {
  aside: {
    homeLabel: "Home ng {{product}}",
    pill: "Gawa para sa mga organisasyon sa Pilipinas",
    headline: "Mga sagot na mapagkakatiwalaan ng inyong mga customer at komunidad — mula sa sarili ninyong mga dokumento.",
    citeTitle: "May pinagmulan ang bawat sagot",
    citeBody: "Nakikita ng mga tao ang mismong pahina ng gabay, patakaran o listahan ng presyo na pinagkunan nito.",
    channelsTitle: "Isang assistant, lahat ng channel",
    channelsBody: "Ang inyong website, web app link, mobile app, at Facebook Messenger.",
    privacyTitle: "Handa sa Data Privacy Act",
    privacyBody: "Pagkuha ng consent, kontrol sa retention, at audit logs na naaayon sa RA 10173 at gabay ng NPC.",
    sampleLabel: "Front Desk Assistant · Widget",
    live: "Live",
    sampleQuestion: "Magkano po ang reservation fee?",
    sampleAnswerLead: "Ang reservation fee ay",
    sampleAnswerAmount: "₱5,000",
    sampleAnswerTail: "at ibabawas sa inyong unang bayad. Puwedeng magbayad sa GCash, Maya o card.",
    sampleSource: "Fees & Payment Guide 2026.pdf · p. 2",
    footer: "© 2026 {{company}} Inc. · Clark Freeport Zone, Pampanga",
    hosting: "Naka-host sa Singapore",
  },
  common: {
    workEmail: "Email sa trabaho",
    password: "Password",
    passwordPlaceholder: "Ilagay ang password",
    showPassword: "Ipakita ang password",
    hidePassword: "Itago ang password",
    or: "o",
    backToSignIn: "Bumalik sa sign in",
    back: "Bumalik",
    signIn: "Mag-sign in",
    alreadyHaveAccount: "May account na?",
    dismiss: "Isara",
    terms: "Mga Tuntunin",
    termsOfService: "Mga Tuntunin ng Serbisyo",
    privacy: "Privacy",
    privacyNotice: "Abiso sa Privacy",
    status: "Status",
    continueGoogle: "Magpatuloy gamit ang Google",
    continueMicrosoft: "Magpatuloy gamit ang Microsoft",
    google: "Google",
    microsoft: "Microsoft",
    notConfigured: "Hindi pa naka-set up",
    securityCheck: "Security check",
    securityCheckHint: "Kumpletuhin ang security check para magpatuloy.",
    securityCheckFailed: "Hindi ma-load ang security check. I-refresh ang page at subukan muli.",
    securityCheckRetry: "Subukan muli",
    emailInvalid: "Maglagay ng wastong email address",
    emailRequired: "Ilagay ang iyong email address",
    emailTooLong: "Masyadong mahaba ang email address na iyan",
    passwordRequired: "Ilagay ang iyong password",
    passwordTooLong: "Masyadong mahaba ang password na iyan",
    nameRequired: "Ilagay ang buo mong pangalan",
    loading: "Naglo-load…",
    opensNewTab: "(bubukas sa bagong tab)",
  },
  password: {
    placeholder: "Hindi bababa sa 10 character",
    hintEmpty: "Gumamit ng 10+ character na may halong letra, numero at simbolo",
    strength0: "Napakahina",
    strength1: "Mahina",
    strength2: "Katamtaman",
    strength3: "Maganda",
    strength4: "Malakas",
    strengthLabel: "Lakas ng password: {{label}}",
    ruleLen: "10+ character",
    ruleCase: "Malaki at maliit na letra",
    ruleNum: "Kahit isang numero",
    ruleSym: "Kahit isang simbolo",
    ruleMatch: "Magkapareho ang mga password",
    rulesLabel: "Mga kailangan sa password",
    ruleMet: "pasado",
    ruleNotMet: "hindi pa pasado",
    tooShort: "Gumamit ng hindi bababa sa 10 character",
    mismatch: "Hindi magkapareho ang mga password",
  },
  otp: {
    digit: "Digit {{n}}",
    incomplete: "Ilagay ang lahat ng 6 na digit",
  },
  login: {
    title: "Mag-sign in · {{product}}",
    newHere: "Bago sa {{product}}?",
    createAccount: "Gumawa ng account",
    heading: "Maligayang pagbabalik",
    subtitle: "Mag-sign in para pamahalaan ang iyong assistant.",
    errorTitle: "Hindi tugma ang email at password.",
    errorAttempts_one: "May {{count}} subok ka pa bago ma-lock ang account nang 15 minuto.",
    errorAttempts_other: "May {{count}} subok ka pa bago ma-lock ang account nang 15 minuto.",
    lockedTitle: "Masyadong maraming maling pag-sign in.",
    lockedBody: "Naka-lock ang account na ito nang 15 minuto. Maaari mong i-reset ang password habang naghihintay.",
    resetLink: "I-reset ang password",
    forgot: "Nakalimutan ang password?",
    remember: "Tandaan ako sa device na ito",
    submit: "Mag-sign in",
    sso: "Mag-sign in gamit ang SSO",
    ssoUnavailable: "Available ang SAML single sign-on sa Pro at Institution. Hilingin sa Owner ng workspace na i-set up ito.",
    ssoTitle: "Mag-sign in gamit ang SSO",
    ssoSub: "Gamitin ang single sign-on ng organisasyon mo. Hahanapin namin ito mula sa domain ng iyong email.",
    ssoContinue: "Magpatuloy gamit ang SSO",
    ssoNone: "Hindi pa naka-set up ng kumpanya mo ang single sign-on para sa address na ito. Mag-sign in gamit ang password.",
    ssoEnforced: "Hinihingi ng workspace mo ang single sign-on. Magpatuloy gamit ang account ng kumpanya.",
    ssoBack: "Bumalik sa sign in",
    ssoError: {
      failed: "Hindi gumana ang single sign-on. Subukan muli o magtanong sa owner ng workspace.",
      unavailable: "Hindi available ang single sign-on para sa workspace na iyon.",
      not_a_member: "Hindi miyembro ng workspace ang account na iyon. Hilingin sa owner na imbitahan ka.",
      domain: "Hindi naka-set up ang e-mail domain na iyon para sa single sign-on.",
      expired: "Natagalan ang pag-sign in. Magsimulang muli.",
    },
    newOrg: "Bagong organisasyon?",
    staffTitle: "Staff ng CRUD.IT?",
    staffBody: "Login ng platform admin",
    yourData: "Ang iyong data",
    oauthFailed: "Hindi natapos ang sign-in sa provider na iyon. Subukan muli o gamitin ang email at password.",
  },
  register: {
    title: "Gumawa ng workspace · {{product}}",
    trial: "14 na araw na libreng trial · walang credit card",
    heading: "Gumawa ng iyong workspace",
    subtitle: "Mag-set up ng AI assistant na sumasagot mula sa sarili mong mga dokumento sa loob ng mga 15 minuto.",
    aboutYou: "Tungkol sa iyo",
    fullName: "Buong pangalan",
    emailHint: "Magpapadala kami ng 6-digit na code para i-verify ang address na ito.",
    yourOrganization: "Ang iyong organisasyon",
    orgName: "Pangalan ng organisasyon",
    orgNameRequired: "Ilagay ang pangalan ng iyong organisasyon",
    workspaceUrl: "URL ng iyong workspace:",
    slugChecking: "Sinusuri kung available…",
    slugTaken: "May gumagamit na ng URL na iyan — dadagdagan namin ito ng suffix.",
    slugAvailable: "Available",
    orgType: "Industriya",
    size: "Laki",
    sizeUnder200: "Wala pang 200 tao",
    size200to500: "200–500 tao",
    size501to1500: "501–1,500 tao",
    size1501to5000: "1,501–5,000 tao",
    sizeOver5000: "Mahigit 5,000 tao",
    country: "Bansa",
    consentTermsLead: "Sumasang-ayon ako sa",
    consentTermsAnd: "at",
    consentDpaLead: "Pumapayag ako sa pagproseso ng aking personal na datos sa ilalim ng",
    consentDpaAct: "Data Privacy Act of 2012",
    consentDpaTail: "(RA 10173) para sa pag-set up ng account at suporta.",
    consentMarketing: "Padalhan ako ng mga balita sa produkto at webinar (opsyonal).",
    consentRequired: "Tanggapin ito para magpatuloy",
    submit: "Gumawa ng workspace",
    orSignUpWith: "o mag-sign up gamit ang",
    trialHeading: "Simulan ang iyong 14 na araw na libreng trial — walang credit card",
    trialSub: "Mag-set up ng AI assistant na sumasagot mula sa sarili mong mga dokumento sa loob ng mga 15 minuto.",
    planBadgeLead: "Sinusubukan ang",
    planBadgeTail: "plan",
    stepOf: "Hakbang {{n}} sa {{total}}",
    close: "Isara",
    emailHelp: "Magpapadala kami ng 6-digit na code para i-verify ang address na ito.",
    emailBad: "Maglagay ng wastong email sa trabaho.",
    continue: "Magpatuloy",
    back: "Bumalik",
    aboutSub: "Ikaw ang magiging owner ng workspace na ito.",
    namePlaceholder: "Juan dela Cruz",
    pwMoreOne: "1 pang character",
    pwMoreMany: "{{count}} pang character",
    pwMix: "Maglagay ng halo ng letra at numero",
    pwWeak: "Mahinang password",
    pwOkay: "Pwedeng password",
    pwGood: "Magandang password",
    pwStrong: "Matibay na password",
    orgSub: "Magsisimula kami sa template para sa iyong industriya. Mapapalitan mo ito anumang oras.",
    orgPlaceholder: "Kalayaan Heights Academy",
    consentsTitle: "Halos tapos na",
    consentsSub: "Ilang kasunduan, tapos handa na ang iyong workspace.",
    required: "Kailangan",
    captchaChecking: "Sinusuri…",
    captchaPassed: "Pumasa ang security check",
    captchaTag: "Turnstile",
    verifyTitle: "Tingnan ang iyong email",
    verifySubLead: "Ilagay ang 6-digit na code na ipinadala namin sa",
    verifyAction: "I-verify",
    resendLead: "Hindi natanggap?",
    resendWait: "Magpadala muli sa {{seconds}}s",
    resendNow: "Magpadala muli ng code",
    changeEmail: "Palitan ang email",
    noCodeLead: "Walang code? Kung may account ka na,",
    noCodeSignIn: "mag-sign in",
    noCodeOr: "o",
    noCodeReset: "i-reset ang iyong password",
    doneTitle: "Handa na ang iyong workspace",
    doneWelcome: "Maligayang pagdating, {{name}}! Handa na ang iyong workspace",
    doneStepsLabel: "Mga susunod na hakbang",
    doneStep1: "Mag-upload ng ilang dokumento",
    doneStep1Hint: "PDF, sheet, slides, scan o ang inyong website",
    doneStep2: "Subukan sa Playground",
    doneStep2Hint: "Magtanong sa English, Filipino o Taglish",
    doneStep3: "I-install ang widget",
    doneStep3Hint: "Widget Studio → Install, tapos i-paste ang snippet",
    doneEta: "~5 min",
    openConsole: "Buksan ang console",
    trialStarted: "Nagsimula na ang 14 na araw na libreng trial. Walang credit card.",
    trust: "Gawa para sa mga organisasyon sa Pilipinas · Handa sa Data Privacy Act · Naka-host sa Singapore · Produkto ng CRUD.IT",
    resumeNote: "Naitago namin ang iyong mga sagot, maliban sa password. Ilagay ulit ito para magpatuloy.",
  },
  verify: {
    title: "I-verify ang email · {{product}}",
    heading: "Tingnan ang iyong inbox",
    sentLead: "Nagpadala kami ng 6-digit na code sa",
    sentTail: "Mag-e-expire ito sa loob ng 10 minuto.",
    yourEmail: "iyong email address",
    codeLabel: "Verification code",
    tip: "Tip: puwede mong i-paste ang buong code.",
    submit: "I-verify ang email",
    didntGetIt: "Hindi natanggap?",
    resendIn: "Magpadala muli sa {{seconds}}s",
    resend: "Magpadala muli ng code",
    resent: "Bagong code ipinadala sa {{email}}",
    wrongAddress: "Maling address?",
    changeEmail: "Palitan ang email",
    spamNote: "Tingnan ang Spam o Promotions folder. Ang email sa managed na company account ay maaaring umabot ng 2 minuto.",
    missingEmail: "Buksan ang link mula sa iyong sign-up, o mag-sign up muli para makakuha ng bagong code.",
  },
  twoFactor: {
    title: "Two-factor verification · {{product}}",
    differentAccount: "Gumamit ng ibang account",
    heading: "Two-factor verification",
    appSubLead: "Buksan ang iyong authenticator app (Google Authenticator, Microsoft Authenticator, o Authy) at ilagay ang 6-digit na code para sa",
    backupSub: "Ilagay ang isa sa mga 8-character na backup code na na-download mo noong in-set up ang two-factor authentication.",
    notYou: "Hindi ikaw?",
    codeLabel: "Authentication code",
    codeHint: "Nagbabago ang code bawat 30 segundo.",
    backupLabel: "Backup code",
    backupHint: "Isang beses lang gumagana ang bawat isa sa 10 backup code mo. Na-save mo ang mga ito noong in-on ang 2FA.",
    backupInvalid: "Ilagay ang 8-character na code, hal. ABCD-1234",
    trust: "Pagkatiwalaan ang device na ito nang 30 araw",
    submit: "I-verify at mag-sign in",
    useBackup: "Gumamit ng backup code",
    useApp: "Gamitin ang authenticator app",
    helpLead: "Nawala ang phone at backup codes? Hilingin sa ibang Owner ng workspace na i-reset ang 2FA, o",
    helpLink: "makipag-ugnayan sa CRUD.IT Support",
    recoverySent: "Naipadala ang recovery request sa CRUD.IT Support — asahan ang identity check sa loob ng 1 business day",
    expired: "Nag-expire na ang sign-in na ito. Mag-sign in muli para makakuha ng bagong code.",
  },
  forgot: {
    title: "Nakalimutan ang password · {{product}}",
    heading: "I-reset ang iyong password",
    subtitle: "Ilagay ang iyong email sa trabaho at padadalhan ka namin ng link para magtakda ng bago.",
    sentMessage: "Kung may account para sa email na iyon, papunta na ang reset link.",
    submit: "Ipadala ang reset link",
    remembered: "Naalala mo na?",
    successHeading: "Tingnan ang iyong inbox",
    successLead: "Kung may account para sa",
    successTail: "makakatanggap ka ng reset link sa loob ng isang minuto. Mag-e-expire ang link sa loob ng 30 minuto.",
    noteLead: "Hindi natanggap? Tingnan ang Spam, o",
    sendAgain: "ipadala muli",
    noteTail: ".",
    team: "Team",
    sentAgain: "Naipadala muli ang reset link",
    throttled: "Masyadong maraming hiling na i-reset. Subukan muli sa loob ng mga {{minutes}} min.",
  },
  reset: {
    title: "Magtakda ng bagong password · {{product}}",
    heading: "Magtakda ng bagong password",
    forLead: "Para sa",
    forTail: "Masa-sign out ka sa ibang mga device.",
    newPassword: "Bagong password",
    confirm: "Kumpirmahin ang bagong password",
    submit: "I-update ang password",
    done: "Na-update ang password. Mag-sign in gamit ang bago mong password.",
    invalidTitle: "Nag-expire na ang reset link na ito",
    invalidBody: "Isang beses lang gumagana ang reset link at nag-e-expire pagkalipas ng 30 minuto. Humiling ng bago para magpatuloy.",
    requestNew: "Humiling ng bagong link",
  },
  invite: {
    title: "Tanggapin ang imbitasyon · {{product}}",
    signInToAccept: "Mag-sign in para tanggapin",
    invitedYou: "Inimbitahan ka ni {{inviter}} na sumali sa {{workspace}} sa {{product}} bilang",
    invitedYouNoInviter: "Iniimbitahan kang sumali sa {{workspace}} sa {{product}} bilang",
    workspace: "Workspace",
    role: "Role",
    invitedBy: "Inimbitahan ni",
    expires: "Mag-e-expire",
    expiresIn_one: "Sa loob ng {{count}} araw · {{date}}",
    expiresIn_other: "Sa loob ng {{count}} araw · {{date}}",
    expiresToday: "Ngayong araw · {{date}}",
    whatCan: "Ano ang magagawa ng {{role}}?",
    cannot: "Hindi makapamahala ng {{modules}}",
    articleA: "isang",
    articleAn: "isang",
    setupHeading: "I-set up ang iyong account",
    joiningAs: "Sasali ka bilang",
    orSetPassword: "o magtakda ng password",
    jobTitle: "Posisyon",
    optional: "(opsyonal)",
    consentLead: "Sumasang-ayon ako sa",
    consentTail: "at pumapayag sa pagproseso ng aking datos sa ilalim ng Data Privacy Act of 2012.",
    submit: "Tanggapin ang imbitasyon at sumali",
    notYou: "Hindi ikaw, o hindi mo ito inaasahan?",
    decline: "Tanggihan ang imbitasyon",
    declined: "Tinanggihan ang imbitasyon — naabisuhan na si {{inviter}}",
    declinedNoInviter: "Tinanggihan ang imbitasyon",
    signedInAs: "Naka-sign in ka bilang {{email}}.",
    acceptSignedIn: "Tanggapin ang imbitasyon",
    mismatchTitle: "Para sa ibang email ang imbitasyong ito",
    mismatchBody: "Naka-sign in ka bilang {{current}}, pero ipinadala ang imbitasyon sa {{invited}}. Mag-sign out at buksan muli ang link.",
    signOut: "Mag-sign out",
    invalidTitle: "Hindi available ang imbitasyong ito",
    invalidBody: "Maaaring nag-expire na, binawi, o natanggap na. Hilingin sa nag-imbita sa iyo na magpadala ng bago.",
    goToSignIn: "Pumunta sa sign in",
    loadFailedTitle: "Hindi namin ma-load ang imbitasyon mo",
    loadFailedBody: "Maaaring gumagana pa ang link. Suriin ang koneksyon mo at subukan muli.",
    retry: "Subukan muli",
    joined: "Maligayang pagdating sa {{workspace}}",
  },
  errors: {
    generic: "May nagkaproblema. Pakisubukan muli.",
    network: "Hindi namin maabot ang server. Tingnan ang iyong koneksyon at subukan muli.",
    rateLimited: "Masyadong maraming subok. Maghintay sandali at subukan muli.",
    invalidCode: "Hindi tama ang code na iyan.",
    invalidCodeAttempts_one: "Hindi tama ang code na iyan. May {{count}} subok ka pa.",
    invalidCodeAttempts_other: "Hindi tama ang code na iyan. May {{count}} subok ka pa.",
    codeExpired: "Nag-expire na ang code na iyan. Humiling ng bago.",
    tooManyAttempts: "Masyadong maraming subok. Humiling ng bagong code.",
    weakPassword: "Gumamit ng hindi bababa sa 10 character para sa password.",
    breachedPassword: "Lumabas na ang password na ito sa isang data breach. Pumili ng ibang password.",
    captchaRequired: "Pakikumpleto ang security check para magpatuloy.",
    captchaFailed: "Hindi pumasa ang security check. Pakisubukan muli.",
    emailTaken: "May account na para sa email na ito. Mag-sign in na lang.",
  },
};
